import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Check, X, Ban, Pencil, CircleCheck, Users, KeyRound, Banknote, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { BOOK_STATUS, WEEKDAYS, fmtBookingWhen, fmtCHF } from "@/lib/format";
import { RoomPhoto } from "@/components/RoomPhoto";
import { MapBadge, StatusBadge } from "@/components/StatusBadge";
import { Card, Empty, PageHeader, Sel, mapOpts } from "@/components/Field";
import { RoomDialog } from "@/components/dialogs/RoomDialog";
import { BookingDialog } from "@/components/dialogs/BookingDialog";
import { ConfirmBookingDialog, canInvoiceBooking } from "@/components/dialogs/ConfirmBookingDialog";

const BookingRow = ({ b, can, act, onEdit, onConfirm }) => (
  <li data-testid={`booking-row-${b.id}`} className="px-5 py-4 space-y-2">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-base font-semibold text-slate-900">{b.room_name} · {fmtBookingWhen(b)}</div>
        {b.total != null && <div data-testid={`booking-total-${b.id}`} className="text-sm font-semibold text-slate-700">{fmtCHF(b.total)}{b.cleaning && " · pulizia inclusa"}{b.invoice_number && <span data-testid={`booking-invoice-${b.id}`} className="font-normal text-slate-500"> · Fattura {b.invoice_number}</span>}</div>}
        <div className="text-sm text-slate-600">{b.requester_name}{b.event_type && ` · ${b.event_type}`}{b.participants && ` · ${b.participants} persone`}</div>
        {can("bookings", "approve") && <div className="text-sm text-slate-500">{[b.requester_email, b.requester_phone].filter(Boolean).join(" · ")}</div>}
        {b.notes && <div className="text-sm text-slate-500 mt-1">{b.notes}</div>}
        {b.status_reason && <div className="text-sm text-slate-500">Motivo: {b.status_reason}</div>}
      </div>
      <div className="flex flex-wrap gap-1.5 justify-end">{b.source === "public" && <StatusBadge label="Modulo pubblico" tone="purple" testId={`booking-public-${b.id}`} />}<MapBadge map={BOOK_STATUS} value={b.status} testId={`booking-status-${b.id}`} /></div>
    </div>
    {can("bookings", "approve") && (
      <div className="flex flex-wrap gap-2">
        {b.status === "pending" && <Button data-testid={`booking-confirm-${b.id}`} className="h-10 bg-green-700 hover:bg-green-800" onClick={() => (canInvoiceBooking(b, can) ? onConfirm(b) : act(b, "confirm", "Prenotazione confermata"))}><Check className="h-4 w-4 mr-1" /> Conferma</Button>}
        {b.status === "pending" && <Button data-testid={`booking-reject-${b.id}`} variant="outline" className="h-10" onClick={() => act(b, "reject", "Prenotazione rifiutata", true)}><X className="h-4 w-4 mr-1" /> Rifiuta</Button>}
        {b.status === "confirmed" && <Button data-testid={`booking-complete-${b.id}`} variant="outline" className="h-10" onClick={() => act(b, "complete", "Segnata come completata")}><CircleCheck className="h-4 w-4 mr-1" /> Completata</Button>}
        {["pending", "confirmed"].includes(b.status) && can("bookings", "edit") && <Button data-testid={`booking-edit-${b.id}`} variant="ghost" className="h-10" onClick={() => onEdit(b)}><Pencil className="h-4 w-4 mr-1" /> Modifica</Button>}
        {["pending", "confirmed"].includes(b.status) && <Button data-testid={`booking-cancel-${b.id}`} variant="ghost" className="h-10 text-red-700" onClick={() => act(b, "cancel", "Prenotazione cancellata", true)}><Ban className="h-4 w-4 mr-1" /> Cancella</Button>}
      </div>
    )}
  </li>
);

const RoomCard = ({ r, canEdit, onEdit }) => (
  <Card testId={`room-card-${r.id}`} className="p-5 space-y-2">
    <RoomPhoto room={r} />
    <div className="flex items-start justify-between gap-2">
      <h3 className="font-heading text-lg font-semibold text-slate-900">{r.name}</h3>
      {r.active ? <StatusBadge label="Attiva" tone="success" /> : <StatusBadge label="Non disponibile" tone="neutral" />}
    </div>
    {r.address && <p className="text-sm text-slate-600">{r.address}</p>}
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
      {r.capacity && <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" /> {r.capacity} persone</span>}
      {r.price_per_day != null && <span className="inline-flex items-center gap-1"><Banknote className="h-4 w-4" /> {fmtCHF(r.price_per_day)} / giorno</span>}
      {r.cleaning_enabled && <span className="inline-flex items-center gap-1"><Sparkles className="h-4 w-4" /> Pulizia {fmtCHF(r.cleaning_price || 0)}</span>}
      {r.keys_info && <span className="inline-flex items-center gap-1"><KeyRound className="h-4 w-4" /> {r.keys_info}</span>}
    </div>
    {r.closed_weekdays?.length > 0 && <p className="text-sm text-slate-500">Chiusa: {r.closed_weekdays.map((d) => WEEKDAYS[d]).join(", ")}</p>}
    {r.rules && <p className="text-sm text-slate-500">Regolamento v{r.rules_version}</p>}
    {canEdit && <Button data-testid={`room-edit-${r.id}`} variant="outline" className="h-10" onClick={() => onEdit(r)}><Pencil className="h-4 w-4 mr-1" /> Modifica</Button>}
  </Card>
);

export default function RoomsPage() {
  const { can } = useAuth();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState("bookings");
  const [f, setF] = useState({ room_id: "", status: sp.get("status") || "" });
  const [roomDialog, setRoomDialog] = useState(null);
  const [bookDialog, setBookDialog] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const rooms = useFetch("/rooms");
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(f).filter(([, v]) => v)));
  const { data: bookings, reload } = useFetch(`/bookings?${qs}`);

  const act = async (b, action, msg, askReason) => {
    const reason = askReason ? window.prompt("Motivo (facoltativo)") : null;
    if (askReason && reason === null) return;
    try {
      await api.post(`/bookings/${b.id}/${action}`, askReason ? { reason: reason || null } : {});
      toast.success(msg);
    } catch (e) {
      toast.error(formatError(e));
    }
    reload();
  };

  return (
    <>
      <PageHeader title="Sale e prenotazioni">
        {tab === "bookings" && can("bookings", "create") && <Button data-testid="bookings-new-button" className="h-11" disabled={!rooms.data?.length} onClick={() => setBookDialog("new")}><Plus className="h-4 w-4 mr-1" /> Nuova prenotazione</Button>}
        {tab === "rooms" && can("rooms", "create") && <Button data-testid="rooms-new-button" className="h-11" onClick={() => setRoomDialog("new")}><Plus className="h-4 w-4 mr-1" /> Nuova sala</Button>}
      </PageHeader>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-11">
          <TabsTrigger value="bookings" className="h-9 px-4" data-testid="rooms-tab-bookings">Prenotazioni</TabsTrigger>
          <TabsTrigger value="rooms" className="h-9 px-4" data-testid="rooms-tab-rooms">Sale</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === "bookings" ? (
        <>
          <Card className="p-4 grid grid-cols-2 gap-2 max-w-xl">
            <Sel testId="bookings-filter-room" value={f.room_id} onChange={(v) => setF({ ...f, room_id: v })} placeholder="Tutte le sale" options={(rooms.data || []).map((r) => ({ value: r.id, label: r.name }))} />
            <Sel testId="bookings-filter-status" value={f.status} onChange={(v) => setF({ ...f, status: v })} placeholder="Tutti gli stati" options={mapOpts(BOOK_STATUS)} />
          </Card>
          {rooms.data?.length === 0 && <Empty testId="rooms-none" text="Crea prima una sala nella scheda “Sale”." />}
          {bookings && bookings.length === 0 && rooms.data?.length > 0 && <Empty testId="bookings-empty" text="Nessuna prenotazione." />}
          {bookings?.length > 0 && <Card><ul className="divide-y">{bookings.map((b) => <BookingRow key={b.id} b={b} can={can} act={act} onEdit={setBookDialog} onConfirm={setConfirming} />)}</ul></Card>}
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rooms.data?.length === 0 && <Empty text="Nessuna sala." />}
          {rooms.data?.map((r) => <RoomCard key={r.id} r={r} canEdit={can("rooms", "edit")} onEdit={setRoomDialog} />)}
        </div>
      )}
      <RoomDialog open={!!roomDialog} room={roomDialog === "new" ? null : roomDialog} onOpenChange={(o) => !o && setRoomDialog(null)} onSaved={rooms.reload} />
      <BookingDialog open={!!bookDialog} booking={bookDialog === "new" ? null : bookDialog} rooms={rooms.data} onOpenChange={(o) => !o && setBookDialog(null)} onSaved={reload} />
      <ConfirmBookingDialog booking={confirming} onOpenChange={(o) => !o && setConfirming(null)} onSaved={reload} />
    </>
  );
}
